import { FC, useCallback, useContext, useEffect, useState, ReactNode } from 'react';
import classnames from 'classnames';
import { useSelector, useDispatch } from 'react-redux';
import { graphql, useFragment } from 'react-relay';
import { useAuctionPricing, useAuctionState } from 'dibs-auctions/exports/hooks';
import { useAuctionBidModalState } from '../hooks/useAuctionBidModalState';
import { trackAuctionBidClick } from 'dibs-auctions-bidding-modal/exports/bidModalTrackingHelpers';
import { updateRelayVariables } from '../../actions/sharedRelayVariableActions';
import { ServerVarsContext } from '../../global/ServerVarsContext/ServerVarsContext';

// components
import { PdpSharedAuctionClosed } from '../PdpSharedAuctionClosed/PdpSharedAuctionClosed';
import { PdpSharedAuctionPreview } from '../PdpSharedAuctionPreview/PdpSharedAuctionPreview';
import { PdpSharedAuctionActionBid } from '../PdpSharedAuctionActions/PdpSharedAuctionActionBid';
import { PdpSharedAuctionUnavailable } from '../PdpSharedAuctionUnavailable/PdpSharedAuctionUnavailable';
import { PdpSharedAuctionSubscription } from '../PdpSharedAuctionSubscription/PdpSharedAuctionSubscription';
import { PdpSharedAuctionActionBinAndBid } from '../PdpSharedAuctionActions/PdpSharedAuctionActionBinAndBid';
import { PdpSharedAuctionActionCompletePurchase } from '../PdpSharedAuctionActions/PdpSharedAuctionActionCompletePurchase';
import { PdpSharedAuctionSecondChanceOffer } from '../PdpSharedAuctionActions/PdpSharedAuctionSecondChanceOffer';
import AuctionBiddingModal from 'dibs-auctions-bidding-modal/exports/AuctionBiddingModal';
import { BID_RESPONSE_ERRORS } from 'dibs-auctions-bidding-modal/exports/constants';

// types
import { PdpSharedAuctionInfo_item$key as Item } from './__generated__/PdpSharedAuctionInfo_item.graphql';
import { OnBidPlaced } from 'dibs-auctions-bidding-modal/exports/types';

// styles
import styles from './PdpSharedAuctionInfo.scss';

type Props = {
    item: Item;
    currency: string;
    promoCode: ReactNode;
};

type RelayVariables = {
    userId: string;
    isClient: boolean;
};

type ReduxStoreType = {
    relayVariables: {
        variables: RelayVariables;
    };
};

const itemFragment = graphql`
    fragment PdpSharedAuctionInfo_item on Item {
        isSold
        serviceId
        isListingNotListed
        isListingUnlisted
        seller {
            isInternal
            isAccessible
        }
        relevantAuctionLot(page: pdp) {
            endDate
        }
        relevantAuctionSolution(page: pdp) {
            reserveMet
            highestBidderId
        }
        lastTransaction(buyerId: $userId) @include(if: $fetchUser) {
            isCanceled
            checkoutDate
            isWinningAuctionOrder
            isSecondChanceOffer
            mostRecentOffer {
                id
                serviceId
                isExpired
            }
        }
        ...PriceDisplay_item @arguments(userId: $userId, isTrade: $isTrade)
        ...useAuctionState_item @arguments(page: pdp)
        ...useAuctionPricing_item @arguments(page: pdp)
        ...PdpSharedAuctionActionBinAndBid_item
        ...PdpSharedAuctionActionBid_item
        ...PdpSharedAuctionClosed_item
        ...PdpSharedAuctionActionCompletePurchase_item
        ...PdpSharedAuctionPreview_item
        ...PdpSharedAuctionSecondChanceOffer_item
        ...bidModalTrackingHelpers_item
    }
`;

export const PdpSharedAuctionInfo: FC<Props> = ({ currency, item: itemRef, promoCode }) => {
    const item = useFragment(itemFragment, itemRef);
    const {
        isPreviewAuction,
        isActiveAuction,
        isPausedAuction,
        isTransitionAuction,
        isUnavailableAuction,
        isSecondChanceOfferSent,
        hasAuctionEndedWithBIN,
        hasAuctionEndedWithWinner,
        hasAuctionEndedWithNoWinner,
    } = useAuctionState({ item });
    const { isBuyItNowEligible } = useAuctionPricing({
        item,
        currency,
    });
    const { userId, isClient } = useSelector<ReduxStoreType, RelayVariables>(
        relay => relay?.relayVariables?.variables || {}
    );
    const { isMobile } = useContext(ServerVarsContext);
    const dispatch = useDispatch();
    const {
        isSold,
        seller,
        lastTransaction,
        isListingUnlisted,
        isListingNotListed,
        relevantAuctionLot,
        relevantAuctionSolution,
    } = item || {};
    const {
        mostRecentOffer,
        isSecondChanceOffer,
        isWinningAuctionOrder,
        isCanceled: isTransactionCanceled,
        checkoutDate: transactionCheckoutDate,
    } = lastTransaction || {};
    const { endDate } = relevantAuctionLot || {};
    const { reserveMet, highestBidderId } = relevantAuctionSolution || {};
    const isUnpurchasable =
        isUnavailableAuction ||
        (hasAuctionEndedWithBIN && !isSold) || // item was sold with BIN and then canceled
        (isListingUnlisted && !(isListingNotListed && !seller?.isInternal)) ||
        !seller?.isAccessible;
    const setupSubscription =
        !hasAuctionEndedWithWinner && !isUnpurchasable && !isPreviewAuction && !isSold;

    const [isBiddingDisabled, setIsBiddingDisabled] = useState<boolean>(
        isTransitionAuction || false
    );
    const { isModalOpen, openModal, closeModal } = useAuctionBidModalState();

    const onAuctionBidClick = (): void => {
        trackAuctionBidClick({ item });
        openModal();
    };

    const [isCloseToReserve, setIsCloseToReserve] = useState(false);

    const onBidPlaced: OnBidPlaced = bidError => {
        if (bidError.errorType === BID_RESPONSE_ERRORS.CLOSE_TO_RESERVE) {
            setIsCloseToReserve(true);
        } else if (isCloseToReserve) {
            setIsCloseToReserve(false);
        }
    };

    const refetch = useCallback(() => {
        dispatch(updateRelayVariables({ updateAuctionItem: true }));
    }, [dispatch]);

    // when countdown timer completes PREVIEW state, refresh page to enter ACTIVE state
    const onPreviewCountdownFinished = useCallback(() => {
        refetch();
    }, [refetch]);

    const onActiveCountdownFinished = useCallback(() => {
        refetch();
        setIsBiddingDisabled(true);
    }, [refetch]);

    useEffect(() => {
        if (!isTransitionAuction) {
            setIsBiddingDisabled(false);
        }
    }, [endDate, isTransitionAuction]);

    let content = null;

    if (isUnpurchasable) {
        content = <PdpSharedAuctionUnavailable />;
    } else if (
        isSecondChanceOffer &&
        !isTransactionCanceled &&
        !isSold &&
        !transactionCheckoutDate
    ) {
        content = <PdpSharedAuctionSecondChanceOffer item={item} currency={currency} />;
    } else if (isPreviewAuction) {
        content = (
            <PdpSharedAuctionPreview
                item={item}
                currency={currency}
                onCountdownFinished={onPreviewCountdownFinished}
                promoCode={promoCode}
            />
        );
    } else if ((isActiveAuction || isTransitionAuction) && isBuyItNowEligible) {
        content = (
            <PdpSharedAuctionActionBinAndBid
                item={item}
                currency={currency}
                onAuctionBidClick={onAuctionBidClick}
                isBiddingDisabled={isBiddingDisabled}
                onCountdownFinished={onActiveCountdownFinished}
            />
        );
    } else if (isActiveAuction || isTransitionAuction) {
        content = (
            <PdpSharedAuctionActionBid
                item={item}
                currency={currency}
                onAuctionBidClick={onAuctionBidClick}
                isBiddingDisabled={isBiddingDisabled}
                onCountdownFinished={onActiveCountdownFinished}
                isCloseToReserve={isCloseToReserve}
            />
        );
    } else if (isPausedAuction || isSold || isSecondChanceOfferSent) {
        content = (
            <PdpSharedAuctionClosed
                item={item}
                currency={currency}
                linkToAuctions={!reserveMet && !isSecondChanceOfferSent}
                linkToBidHistory={!!reserveMet && !isSecondChanceOfferSent}
            />
        );
    } else if (hasAuctionEndedWithWinner && isClient) {
        content =
            highestBidderId === userId &&
            (!lastTransaction ||
                (isWinningAuctionOrder &&
                    !transactionCheckoutDate &&
                    !mostRecentOffer?.isExpired)) ? (
                <PdpSharedAuctionActionCompletePurchase item={item} currency={currency} />
            ) : (
                <PdpSharedAuctionClosed item={item} currency={currency} linkToBidHistory />
            );
    } else if (hasAuctionEndedWithNoWinner) {
        content = <PdpSharedAuctionClosed item={item} currency={currency} linkToAuctions />;
    }
    return (
        <>
            {content && (
                <div
                    className={classnames(styles.container, {
                        [styles.divider]: !isSold && !isUnpurchasable,
                    })}
                >
                    {content}
                </div>
            )}
            {!isPreviewAuction && promoCode}
            {setupSubscription && (
                <PdpSharedAuctionSubscription refetch={refetch} endDate={endDate} />
            )}
            {isModalOpen && (
                <AuctionBiddingModal
                    currency={currency}
                    itemId={item?.serviceId || ''}
                    onClose={closeModal}
                    isMobile={!!isMobile}
                    isBiddingDisabled={isBiddingDisabled}
                    onBidPlaced={onBidPlaced}
                />
            )}
        </>
    );
};
